import { Check, User, PawPrint } from "lucide-react";
import { PunchFist } from "@/components/PunchFist";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n/I18nProvider";
import { cn } from "@/lib/utils";
import { Mode, Pack, getPrice, MODE_LABEL_KEY, MODE_DESC_KEY } from "@/lib/packPricing";

const MODES: { id: Mode; icon: typeof User }[] = [
  { id: "portrait", icon: User },
  { id: "pet", icon: PawPrint },
];

export const Pricing = () => {
  const { t } = useI18n();
  const [mode, setMode] = useState<Mode>("portrait");

  const tiers: { id: Pack; desc: string; popular: boolean }[] = [
    { id: "single", desc: t("pricing.singleDesc"), popular: false },
    { id: "triple", desc: t("pricing.tripleDesc"), popular: true },
  ];

  return (
    <section id="pricing" className="py-20 relative">
      <div className="container">
        <div className="text-center mb-8 space-y-3">
          <h2 className="font-display text-4xl md:text-5xl font-black">
            <span className="text-gradient">{t("pricing.title")}</span>
          </h2>
        </div>

        {/* Mode toggle */}
        <div className="flex flex-wrap justify-center gap-2 mb-3 max-w-3xl mx-auto">
          {MODES.map(({ id, icon: Icon }) => {
            const active = mode === id;
            return (
              <button
                key={id}
                onClick={() => setMode(id)}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold border-2 transition",
                  active
                    ? "border-neon-green text-neon-green glow-green bg-neon-green/10"
                    : "border-border text-muted-foreground hover:border-neon-purple/60"
                )}
              >
                <Icon className="w-4 h-4" />
                {t(MODE_LABEL_KEY[id])}
              </button>
            );
          })}
        </div>
        <p className="text-center text-xs text-muted-foreground mb-10">{t(MODE_DESC_KEY[mode])}</p>

        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {tiers.map((tier) => (
            <div
              key={tier.id}
              className={cn(
                "relative rounded-2xl border p-8 bg-card/60 backdrop-blur transition-all hover:-translate-y-1",
                tier.popular ? "border-neon-green glow-green" : "border-border hover:border-neon-purple/50"
              )}
            >
              {tier.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold bg-neon-green text-background uppercase tracking-wider">
                  {t("pricing.popular")}
                </div>
              )}
              <h3 className="font-display text-2xl font-black flex items-center gap-2">
                <PunchFist variant={tier.id === "single" ? "green" : "purple"} />
                {t(`pricing.${tier.id}`)}
              </h3>
              <p className="text-muted-foreground mt-1 text-sm">{tier.desc}</p>
              <div className="mt-6 mb-6">
                <span className="font-display text-5xl font-black text-gradient">{getPrice(tier.id, mode)}</span>
              </div>
              <ul className="space-y-2.5 text-sm mb-8">
                {[t("pricing.feat1"), t("pricing.feat2"), t("pricing.feat3"), t("pricing.feat4")].map((f) => (
                  <li key={f} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-neon-green shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => {
                  window.dispatchEvent(
                    new CustomEvent("select-pack", { detail: { pack: tier.id, mode } })
                  );
                  document.getElementById("upload")?.scrollIntoView({ behavior: "smooth" });
                }}
                className={cn(
                  "w-full font-bold",
                  tier.popular
                    ? "bg-neon-green text-background hover:bg-neon-green/90"
                    : "bg-neon-purple/10 text-neon-purple hover:bg-neon-purple hover:text-background border border-neon-purple/40"
                )}
              >
                {t("pricing.buy")}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
